import { useState } from 'react'

import { Line } from '../../models/Line'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Checkbox } from '../common/fields/Checkbox'
import { CurrencyField } from '../common/fields/CurrencyField'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { RemovableItemContainer } from './RemovableItemContainer'
import { SelectedItem } from './SelectedItem'
import { WorkflowIconSection } from './WorkflowIconSection'

type ItemLineFields = 'amount' | 'quantity' | 'description' | 'salesTax' | 'discount'

type ItemLineProps = {
    line: Line
    onUpdate: (line: Line) => void
    onRemove: () => void
    secondaryTitle?: string
    disabled?: boolean
    showRate?: boolean
    shownFields?: ItemLineFields[]
}

export const ItemLine = ({
    line,
    onUpdate,
    onRemove,
    secondaryTitle,
    disabled = false,
    showRate = false,
    shownFields = ['amount', 'quantity', 'description', 'salesTax', 'discount'],
}: ItemLineProps) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)

    const handleUpdateQuantity = (newQuantity: number) => {
        onUpdate(line.clone({ quantity: newQuantity, total: newQuantity * (line.rate ?? 0) }))
    }

    const handleUpdateMemo = (newMemo: string) => {
        onUpdate(line.clone({ memo: newMemo }))
    }

    return (
        <>
            <RemovableItemContainer onRemove={!disabled ? onRemove : undefined}>
                <SelectedItem
                    className={`${disabled ? 'pointer-events-none opacity-40' : ''}`}
                    title={line.item?.name ?? 'Custom amount'}
                    subtitle={showRate ? currencyFormat(line.rate, true) : undefined}
                    quantity={shownFields.includes('quantity') ? (line.quantity ?? 1) : undefined}
                    onQuantityChange={handleUpdateQuantity}
                    secondaryTitle={
                        !secondaryTitle ? currencyFormat(line.total, true) : secondaryTitle
                    }
                    primaryButtonTitle="Edit"
                    onClickPrimaryButton={() => setIsEditModalOpen(true)}
                    amount={shownFields.includes('amount') ? (line.total ?? 0) : undefined}
                    onAmountChange={(newTotal) => onUpdate(line.clone({ total: newTotal }))}
                    tertiaryView={
                        line.item?.iconConfig === undefined ? undefined : line.item?.iconConfig
                              .type === 'image' ? (
                            <img
                                src={line.item.iconConfig.uri}
                                className="size-8 rounded-full border-2 border-white object-cover"
                            />
                        ) : (
                            <Icon
                                icon={line.item.iconConfig.icon}
                                background="round"
                                className="size-9 border-2 border-white"
                            />
                        )
                    }
                />
            </RemovableItemContainer>

            <Modal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title="Edit item"
                size="large"
                footer={
                    <div className="justif-end flex items-center">
                        <Button intent="primary" onClick={() => setIsEditModalOpen(false)}>
                            Done
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col items-stretch justify-start gap-y-8">
                    {shownFields.includes('amount') && (
                        <WorkflowIconSection title="Amount" icon="dollar9.large">
                            <CurrencyField
                                type="number"
                                size="medium"
                                min={0}
                                isFullWidth
                                value={line.total ?? 0}
                                onChange={(newTotal) => onUpdate(line.clone({ total: newTotal }))}
                            />
                        </WorkflowIconSection>
                    )}

                    {shownFields.includes('quantity') && (
                        <WorkflowIconSection title="Quantity" icon="hash.large">
                            <TextField
                                type="number"
                                size="medium"
                                min={0}
                                isFullWidth
                                value={line.quantity ?? 1}
                                onChange={(event) =>
                                    handleUpdateQuantity(parseInt(event.target.value))
                                }
                            />
                        </WorkflowIconSection>
                    )}

                    {shownFields.includes('description') && (
                        <WorkflowIconSection
                            title="Description"
                            icon="note.large"
                            secondaryText={`${(line.memo ?? line.item?.memo ?? '').length} / 900`}
                        >
                            <TextArea
                                value={line.memo ?? line.item?.memo ?? ''}
                                onChange={(event) => handleUpdateMemo(event.target.value)}
                                maxLength={900}
                                className="max-h-[170px] min-h-[74px]"
                                size="medium"
                                onClear={() => handleUpdateMemo('')}
                                placeholder="Add a description"
                                isAutoResizing
                            />
                        </WorkflowIconSection>
                    )}

                    {shownFields.includes('salesTax') && (
                        <WorkflowIconSection
                            title="Add sales tax"
                            icon="percentage2.large"
                            secondaryView={
                                <Checkbox
                                    isChecked={line.taxable ?? line.item?.allowSalesTax ?? true}
                                    onChange={(newTaxable) =>
                                        onUpdate(line.clone({ taxable: newTaxable }))
                                    }
                                />
                            }
                            hasHoverEffect
                        />
                    )}

                    {shownFields.includes('discount') && (
                        <WorkflowIconSection
                            title="Allow discount"
                            icon="discount.large"
                            secondaryView={
                                <Checkbox
                                    isChecked={line.discountable ?? true}
                                    onChange={(newDiscountable) =>
                                        onUpdate(line.clone({ discountable: newDiscountable }))
                                    }
                                />
                            }
                            hasHoverEffect
                        />
                    )}
                </div>
            </Modal>
        </>
    )
}
